import { format, isToday, isYesterday, startOfDay } from 'date-fns';
import React, { useEffect, useMemo } from 'react';

import { EventItem } from './EventItem';
import { DomainEvent } from './types';

interface EventListProps {
  events: DomainEvent[];
}

interface GroupedEvents {
  date: Date;
  label: string;
  events: DomainEvent[];
}

const getDateLabel = (date: Date): string => {
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  return format(date, 'EEE, MMM d');
};

const groupEventsByDate = (events: DomainEvent[]): GroupedEvents[] => {
  const groups = new Map<string, GroupedEvents>();

  events.forEach((event) => {
    const eventDate = new Date(event.occurred_at || '');
    const dayStart = startOfDay(eventDate);
    const key = dayStart.toISOString();

    let group = groups.get(key);
    if (!group) {
      group = {
        date: dayStart,
        label: getDateLabel(dayStart),
        events: [],
      };
      groups.set(key, group);
    }

    group.events.push(event);
  });

  // Sort groups by date (newest first)
  return Array.from(groups.values()).sort(
    (a, b) => b.date.getTime() - a.date.getTime(),
  );
};

export const EventList: React.FC<EventListProps> = React.memo(({ events }) => {
  const groupedEvents = useMemo(() => groupEventsByDate(events), [events]);
  const now = new Date();

  // Handle Deep Linking and Highlighting for Events
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const eventId = params.get('event_id');

    if (eventId) {
      // Ensure the element exists before trying to scroll
      // Use a small timeout to allow rendering to complete, similar to TaskUpdates
      setTimeout(() => {
        const element = document.getElementById(`event-${eventId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.classList.add(
            'bg-yellow-50',
            'transition-colors',
            'duration-1000',
            '-mx-4', // Negative margin to highlight full width
            'px-4', // Restore padding
            'rounded-md',
          );

          // Remove highlight after animation and clean up URL
          setTimeout(() => {
            element.classList.remove(
              'bg-yellow-50',
              '-mx-4',
              'px-4',
              'rounded-md',
            );
            params.delete('event_id');
            // Also clean up tab param if desired, but usually keeping tab state is fine
            // params.delete('tab');
            const newUrl = `${window.location.pathname}?${params.toString()}`;
            window.history.replaceState(null, '', newUrl);
          }, 3000);
        }
      }, 500); // Slightly longer timeout to ensure list is fully rendered
    }
  }, [events]); // Re-run when events change (loaded)

  return (
    <div className="flow-root">
      {groupedEvents.map((group, groupIndex) => (
        <div
          key={group.date.toISOString()}
          className={groupIndex > 0 ? 'mt-3' : ''}
        >
          {/* Minimalist date header */}
          <div className="sticky top-0 z-10 flex items-center mb-2 bg-white/95 backdrop-blur-sm py-1 border-b border-zinc-50">
            <span className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wide">
              {group.label}
            </span>
          </div>

          {/* Events for this date */}
          <ul className="space-y-2">
            {group.events.map((event, index) => (
              <div key={event.id} id={`event-${event.id}`}>
                <EventItem
                  event={event}
                  now={now}
                  isLast={
                    groupIndex === groupedEvents.length - 1 &&
                    index === group.events.length - 1
                  }
                />
              </div>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
});

EventList.displayName = 'EventList';
